import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';

// Global styles for Matrix-inspired background
const FutureGlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Space+Grotesk:wght@300;400;500;600;700&family=Orbitron:wght@400;500;600;700;800;900&family=Audiowide:wght@400&family=Exo+2:wght@300;400;500;600;700&display=swap');
  
  body {
    background: #000000;
    overflow-x: hidden;
  }
`;

// Matrix-style data streams - optimized for performance
const matrixRain = keyframes`
  0% { 
    transform: translateY(-140vh); // Start higher
    opacity: 1; 
  }
  100% { 
    transform: translateY(140vh); // End much lower
    opacity: 0; 
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const subtleGlow = keyframes`
  0%, 100% { 
    text-shadow: 0 0 10px rgba(0, 255, 65, 0.3);
  }
  50% { 
    text-shadow: 0 0 20px rgba(0, 255, 65, 0.5);
  }
`;

// Styled Components
const FutureContainer = styled.div`
  min-height: 100vh;
  background: #000000;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow-x: hidden;
`;

// Separate overlay container for matrix effect
const MatrixOverlay = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 140vh !important; // Extended further
  pointer-events: none !important;
  z-index: 999999 !important;
  overflow: visible !important;
  display: block !important;
  visibility: visible !important;
`;

const MatrixColumn = styled.div`
  position: absolute !important;
  top: -140vh; // Start higher
  width: 40px;
  height: 280vh; // Much taller
  left: ${props => props.left}%;
  animation: ${matrixRain} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
  font-family: 'JetBrains Mono', monospace;
  font-size: 24px;
  color: #00ff41 !important;
  line-height: 1.0;
  opacity: ${props => {
    switch(props.layer) {
      case 'top': return '1.0';
      case 'front': return '0.9';
      case 'mid': return '0.8';
      default: return '0.6';
    }
  }} !important;
  text-shadow: 
    0 0 20px #00ff41, 
    0 0 30px #00ff41, 
    0 0 40px #00ff41,
    0 0 50px #00ff41 !important;
  font-weight: 700 !important;
  pointer-events: none !important;
  z-index: ${props => {
    switch(props.layer) {
      case 'top': return '4';
      case 'front': return '3';
      case 'mid': return '2';
      default: return '1';
    }
  }} !important;
  filter: brightness(1.8) saturate(1.5) !important;
  display: block !important;
  visibility: visible !important;
`;

const Header = styled.header`
  text-align: center;
  padding: 8rem 2rem 4rem;
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const MainTitle = styled.h1`
  font-family: 'Inter', sans-serif;
  font-size: clamp(2rem, 6vw, 3.5rem);
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  color: #ffffff;
  line-height: 1.2;
  animation: ${fadeInUp} 1s ease-out;
  position: relative;
  z-index: 1;
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #00ff41;
  margin: 0;
  font-weight: 400;
  animation: ${subtleGlow} 3s ease-in-out infinite;
  animation-delay: 0.5s;
  position: relative;
  z-index: 1;
`;

const ContentGrid = styled.div`
  max-width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  z-index: 1;
`;

const SectionCard = styled.div`
  background: ${props => props.isTransparent ? 'transparent' : 'rgba(0, 0, 0, 0.08)'};
  backdrop-filter: ${props => props.isTransparent ? 'none' : 'blur(8px)'};
  border: none;
  padding: 4rem 2rem;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.8s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  z-index: 1;
  
  /* Removed hover effects */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.02) 0%, 
      rgba(255, 255, 255, 0.01) 50%, 
      rgba(0, 255, 65, 0.02) 100%
    );
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
    min-height: 50vh;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.fontFamily || 'Inter'}, sans-serif;
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: ${props => props.fontWeight || '700'};
  margin: 0 0 2rem 0;
  color: #ffffff;
  line-height: 1.2;
  max-width: 1000px;
  letter-spacing: ${props => props.letterSpacing || '0'};
  text-transform: ${props => props.textTransform || 'none'};
  position: relative;
  z-index: 1;
`;

const SectionContent = styled.p`
  font-family: ${props => props.fontFamily || 'Inter'}, sans-serif;
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  line-height: 1.6;
  color: #a1a1aa;
  margin: 0;
  font-weight: ${props => props.fontWeight || '400'};
  max-width: 1200px;
  position: relative;
  z-index: 1;
`;

const BackButton = styled.button`
  position: fixed;
  top: 2rem;
  left: 2rem;
  background: rgba(15, 15, 15, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  z-index: 10000; // Above matrix
  
  &:hover {
    background: rgba(25, 25, 25, 0.95);
    border-color: rgba(0, 255, 65, 0.4);
    transform: translateY(-1px);
  }
`;

const YearBadge = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(0, 255, 65, 0.1);
  border: 1px solid rgba(0, 255, 65, 0.3);
  color: #00ff41;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 1rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
  animation: ${subtleGlow} 4s ease-in-out infinite;
  z-index: 1;
  
  @media (max-width: 768px) {
    top: 1.5rem;
    right: 1.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
`;

const FuturePage = () => {
  const [matrixColumns, setMatrixColumns] = useState([]);

  useEffect(() => {
    // Generate Matrix-style character columns with balanced performance
    const columns = Array.from({ length: 40 }, (_, i) => { // Increased back to 40 for better coverage
      const random = Math.random();
      let layer;
      if (random > 0.85) layer = 'top';      // 15% - highest layer
      else if (random > 0.65) layer = 'front'; // 20% - overlaps cards
      else if (random > 0.35) layer = 'mid';   // 30% - middle layer
      else layer = 'back';                     // 35% - background layer
      
      return {
        id: i,
        left: (i * 2.5) + Math.random() * 1, // Adjusted distribution (100/40 = 2.5)
        duration: 6 + Math.random() * 8, // Back to varied durations for more natural look
        delay: Math.random() * 15, // Keep optimized delay range
        characters: generateMatrixText(),
        layer: layer
      };
    });
    setMatrixColumns(columns);
  }, []);

  const generateMatrixText = () => {
    // Optimized character set and length for better performance
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZアイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    return Array.from({ length: 40 }, () => // Reduced from 50 to 40 characters per column
      chars.charAt(Math.floor(Math.random() * chars.length))
    ).join('');
  };

  const sections = [
    {
      title: "Augmented Reality and Virtual Reality",
      year: "2030",
      content: "AR software to let users interact with reality and enhance their experience. VR for when in the comfort of their homes and offices, they want to immerse in simulated computer worlds",
      delay: "0s",
      titleFont: "Inter",
      titleWeight: "700",
      titleLetterSpacing: "0em",
      contentFont: "Inter",
      contentWeight: "400"
    },
    {
      title: "Software for Mushroom Computers",
      year: "2038",
      content: "Mushrooms form an internet of connections underground, used to communicate with one another using chemical signals. We will develop software that can be run on these networks by 2038, allowing any human being to connect to natural networks and run computer applications on the very ground we step on. Being able to get real time data of entire forests transmitted and gathered by these organic computers. But also, allowing humans to grow organic computers made up of mushrooms into custom pieces of technology powered by and shaped in form and behaviour by our software.",
      delay: "0.5s",
      titleFont: "Space Grotesk",
      titleWeight: "600",
      titleLetterSpacing: "-0.02em",
      contentFont: "Inter",
      contentWeight: "300"
    },
    {
      title: "Software for Deep Exploration of the Seas",
      year: "2040", 
      content: "The seas most likely hold the secrets of the origin of life on earth. But the harsh conditions of deep sea exploration have made it expensive and difficult. With swarms of reusable and disposable wetware, we aim to explore the oceans like never before, and give researchers and hobbyists the possibility of exploring the depths from the convenience of their own home. With remote live view solutions and realtime AI data analysis, we aim to help discover more species in the oceans than there currently are known ones by 2040.",
      delay: "1s",
      titleFont: "Inter",
      titleWeight: "800",
      titleLetterSpacing: "-0.03em",
      contentFont: "Inter",
      contentWeight: "300"
    },
    {
      title: "Solutions for Space Exploration", 
      year: "2045",
      content: "Space is the final frontier, and cheap has never been synonym with it. But our functional bootstraped approach to space exploration will bring effective solutions to this industry powered by software, wetware and hardware that's disposable, reusable and highly customizable on the go by 2045.",
      delay: "1.5s",
      titleFont: "Orbitron",
      titleWeight: "700",
      titleLetterSpacing: "0.05em",
      titleTransform: "uppercase",
      contentFont: "Exo 2",
      contentWeight: "400"
    },
    {
      title: "Personal Tech for the Atomic Human",
      year: "2050",
      content: "It is the mission of our business to empower human beings as individuals. We don't care about the masses, the mob mentality has hurt our race more than it has helped us. Human beings as individuals will be supressed no more, and any individual on planet earth will have affordable to them, fully customizable software, wetware and hardware that will obey no one but it's owner. True freedom will bring a lot of responsibility and the burden on society to endure the chaos that will follow it. But we have chosen to support the idea of a new generation of atomic humans, that will be able to create ANY piece of technology they desire, fully custom and personalized made in their own garage. Yes, this will be very dangerous, but we believe in giving individuals the opportunity to live without getting babysat by corporate and government overlords.",
      delay: "2s",
      titleFont: "Audiowide",
      titleWeight: "400",
      titleLetterSpacing: "0.1em",
      contentFont: "Space Grotesk",
      contentWeight: "400"
    }
  ];

  return (
    <>
      <FutureGlobalStyles />
      <FutureContainer>
        <BackButton onClick={() => window.history.back()}>
          ← Back
        </BackButton>
        
        <Header>
          <MainTitle>
            What will B&B Global Solutions build in the future?
          </MainTitle>
          <Subtitle>
            Innovative solutions for tomorrow's challenges
          </Subtitle>
        </Header>
        
        <ContentGrid>
          {sections.map((section, index) => (
            <SectionCard 
              key={index} 
              delay={section.delay}
              isTransparent={index % 2 === 1} // Alternate: even=semi-transparent, odd=transparent
            >
              <YearBadge>{section.year}</YearBadge>
              <SectionTitle 
                fontFamily={section.titleFont}
                fontWeight={section.titleWeight}
                letterSpacing={section.titleLetterSpacing}
                textTransform={section.titleTransform}
              >
                {section.title}
              </SectionTitle>
              <SectionContent 
                fontFamily={section.contentFont}
                fontWeight={section.contentWeight}
              >
                {section.content}
              </SectionContent>
            </SectionCard>
          ))}
        </ContentGrid>
      </FutureContainer>
      
      {/* Matrix overlay placed completely outside main container */}
      <MatrixOverlay>
        {matrixColumns.map(column => (
          <MatrixColumn
            key={column.id}
            left={column.left}
            duration={column.duration}
            delay={column.delay}
            layer={column.layer}
          >
            {column.characters}
          </MatrixColumn>
        ))}
      </MatrixOverlay>
    </>
  );
};

export default FuturePage; 